<template>
  <div :class="getBoxClass">
    <div class="box__top">
      <div class="box__label">
        <span>{{ getBoxLabelText }}</span>
      </div>
      <InfoTooltip :tooltipName="`box_${box.id}_tooltip`" :tooltipAdditionalTheme="getTooltipTheme">
        <template v-slot:tooltip_title>{{ getTooltipText("title") }}</template>
        <p v-html="getTooltipText('text')"></p>
      </InfoTooltip>
    </div>
    <div class="box__title">{{ $t(`shop.box_${box.id}.title`) }}</div>
    <div class="box__favorite">
      <button :class="getFavoriteClass" @click="toggleFavorite">
        <div class="box__favorite__icon">
          <i class="icon icon-outer icon-star"></i>
          <i class="icon icon-inner icon-star"></i>
        </div>
        <span>{{ getFavoriteText }}</span>
      </button>
    </div>

    <div class="box__prizes">
      <div
        v-for="(item, index) in getBoxFeaturedContent"
        :key="index"
        :class="getPrizeItemClass(item)"
      >
        <img :src="getPrizeImageSrc(item.reward.item.id)" />

        <i v-if="isPrizeItemFetched(item)" class="icon icon-check"></i>
      </div>
      <button class="box__prizes__button gtm-button" @click="showItemsList">
        {{ $t("shop.button") }}
      </button>
    </div>

    <ShopItemBuy
      :boxContent="getBoxContent"
      :isBoxForVictory="isBoxForVictory"
      :currency="box.conditions.currencies[0].currency"
      @openBoxGTM="openBoxGTM"
    />

    <img class="box__image" :src="getImageSrc" :alt="$t(`shop.box_${box.id}.title`)" />

    <ShopListItemPrizes
      v-if="show_box_items"
      :boxId="box.id"
      :listItems="getBoxContent.rewards"
      :isRepeatable="box.is_repeatable"
      scrollClass="box__prizes__list"
      @closeItemsList="closeItemsList"
      @stopBoxesScroll="stopBoxesScroll"
    />
  </div>
</template>
<script>
import ShopItemBuy from "./ShopItemBuy";
import ShopListItemPrizes from "./ShopListItemPrizes";
import InfoTooltip from "@/components/Common/InfoTooltip";
import ThrowGTMEvent from "@model/helpers/ThrowGTMEvent";

import WindowSizeMixin from "@/components/Common/WindowSizeMixin";

import { mapGetters } from "vuex";

const ITEMS_COUNT_DESKTOP = 4;
const ITEMS_COUNT_MOBILE = 3;

export default {
  name: "ShopListItem",

  components: {
    ShopItemBuy,
    ShopListItemPrizes,
    InfoTooltip,
  },

  mixins: [WindowSizeMixin],

  props: {
    box: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      show_box_items: false,
    };
  },

  computed: {
    ...mapGetters("shop", ["getShopView", "isFavoriteEmpty"]),

    getBoxContent() {
      return this.$store.getters["shop/getBoxContent"](this.box.id);
    },
    isShowThreeItems() {
      return this.window_width < 980;
    },
    getBoxFeaturedContent() {
      let count = this.isShowThreeItems ? ITEMS_COUNT_MOBILE : ITEMS_COUNT_DESKTOP;
      return this.getBoxContent.rewards.slice(0, count);
    },
    isBoxRare() {
      return this.box.is_rare;
    },
    isBoxTemporary() {
      return this.box.is_temporary;
    },
    isBoxForVictory() {
      return ["victory", "victory_vip"].includes(this.box.conditions.currencies[0].currency);
    },
    isBoxWithSkins() {
      return this.box.id === 2;
    },
    getBoxClass() {
      let additional_class = "";
      if (this.isBoxRare) additional_class = "rare";
      if (this.isBoxTemporary) additional_class = "temporary";
      if (this.isBoxForVictory) additional_class = "victory";

      return `shop__list__item box ${additional_class}`;
    },
    getTooltipTheme() {
      let theme = "";
      if (this.isBoxRare) theme = "wf-bp-gold";
      if (this.isBoxForVictory) theme = "wf-bp-violet";
      if (this.isBoxForVictory) theme = "wf-bp-orange";

      return theme;
    },

    getBoxLabelType() {
      let label_type = "common";
      if (this.isBoxRare) label_type = "rare";
      if (this.isBoxTemporary) label_type = "temporary";
      if (this.isBoxForVictory) label_type = "victory";

      return label_type;
    },
    getBoxLabelText() {
      return this.$t(`shop.${this.getBoxLabelType}`);
    },
    isFavorite() {
      return this.box.favorite;
    },
    getFavoriteClass() {
      return this.isFavorite ? "type-favorite" : "";
    },
    getFavoriteText() {
      return this.isFavorite ? this.$t("shop.is_in_favorite") : this.$t("shop.add_to_favorite");
    },
    getImageSrc() {
      try {
        return require(`@/assets/shop/images/box_${this.box.id}.png`);
      } catch {
        return require(`@/assets/shop/images/box_default.png`);
      }
    },
  },

  methods: {
    toggleFavorite() {
      let action = this.isFavorite ? "shop/removeFromFavorite" : "shop/addToFavorite";
      const gtm_event = this.isFavorite ? "ch.favorite.remove" : "ch.favorite.add";

      this.$store.dispatch(action, this.box.id).then(() => {
        if (this.isFavoriteEmpty) {
          this.$store.commit("shop/writeShopViewType", "list");
        }
      });
      this.generateGTMEvent(gtm_event);
    },
    showItemsList() {
      this.show_box_items = true;
      this.generateGTMEvent("ch.card.kit");
    },
    closeItemsList() {
      this.show_box_items = false;
    },
    isPrizeItemFetched(item) {
      return !this.box.is_repeatable && item.fetched;
    },
    getPrizeItemClass(item) {
      return [
        "box__prizes__item",
        this.isPrizeItemFetched(item) ? "item-fetched" : "",
        this.isBoxWithSkins ? "" : "type-rotate",
      ];
    },
    getPrizeImageSrc(img_id) {
      if (!Number.isNaN(Number(img_id))) {
        return `https://wf.cdn.gmru.net/static/wf.mail.ru/img/main/items/achive_${img_id}.png`;
      }
      return `https://wf.cdn.gmru.net/static/wf.mail.ru/img/main/items/${img_id}.png`;
    },
    getTooltipText(text_type) {
      let text = "";

      if (text_type === "title") {
        text = this.isBoxForVictory
          ? this.$t(`shop.warnings.${text_type}.victory`)
          : this.$t(`shop.warnings.${text_type}.box`);
      } else {
        text = this.$t(`shop.box_${this.box.id}.tooltip`);
      }

      return text;
    },
    openBoxGTM(currency) {
      this.generateGTMEvent("ch.card.open", currency);
    },

    generateGTMEvent(gmt_event, currency_type = false) {
      const box_keys = [
        "camo",
        "skin",
        "achievement",
        "gilboa_snake",
        "vip_for_wins",
        "crate_for_wins",
        "randon_weapon",
      ];

      let event_object = {
        card_type: this.getBoxLabelType,
        card_name: box_keys[this.box.id],
      };

      if (currency_type) event_object["currency_type"] = currency_type;

      ThrowGTMEvent(gmt_event, event_object);
    },

    stopBoxesScroll() {
      this.$emit("stopBoxesScroll");
    },
  },
};
</script>
